import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { NULL_SELECTED_VALUE } from '@agent-ds/shared/constants';
import { CompanyDetail, Job, JobAzCheckErrorDetail, JobAzCheckResponse, JobAzCheckWageErrorDetail } from '@agent-ds/shared/interfaces';
import { DynamicField, RowMeta } from '@agent-ds/shared/models';
import { CompanyApiService, DialogService, DynamicFieldService, JobApiService, MasterApiService } from '@agent-ds/shared/services';
import {
  clearFixedLateNightOvertimeCategoryValues,
  clearFixedOvertimeCategoryValues,
  forceFixedOvertimeFeeNoneForDeemedWorkingHours,
  formatVariableWorkingHoursSystemDescription,
  isDeemedWorkingHoursSystemSelected,
} from '../../../util/job-salary-table';
import { SalaryTableRowsService } from '../../../services/salary-table-rows.service';
import { deepClone, deepCompare } from '@agent-ds/shared/util/util';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {
  AzCheckRow,
  AzCheckSection,
  ENTERPRISE_PREFIX,
  JobCategory,
  SALARY_TABLE_FIELD_KEY_PREFIX,
  SEMINAR_AND_INTERVIEW_INFO_FIELD_KEY,
  WAGE_CHECK_FIELD_LABELS,
  WAGE_CHECK_FIELDS,
  WAGE_PREFIX,
  WageCheckField,
} from '@agent-ds/shared/interfaces/job-az-check';

// salaryTableはgetFormRowsが月給・固定残業代・固定残業代補足・固定深夜割増手当・変形労働時間制・給与補足の6行を返すため、
// 通常のfieldName単位のfieldKey（=salaryTable）では6行すべてが同じキーになり、consultationTargets/getErrorが衝突する。
// 行ごとに一意なfieldKeyを割り当てるためSALARY_TABLE_FIELD_KEY_PREFIXを使用する
// salaryTable の6行それぞれの title（日本語）と英語のマッピング
const JA_TO_EN_SALARY_TABLE_ROW_TITLE_MAP: { [title: string]: string } = {
  '月給': 'salary',
  '固定残業代': 'fixedOvertimeFee',
  '固定残業代補足': 'fixedOvertimeFeeDescription',
  '固定の深夜割増手当': 'fixedLateNightOvertime',
  '変形労働時間制': 'variableWorkingHoursSystem',
  '給与補足': 'salaryDescription',
}

// addressはgetFormRowsが郵便番号・都道府県、市区町村、丁目番地号、建物名・部屋番号の4行を返すため、
// 通常のfieldName単位のfieldKey（=address）では4行すべてが同じキーになり、consultationTargets/getErrorが衝突する。
// 行ごとに一意なfieldKeyを割り当てるためJA_TO_EN_ADDRESS_ROW_TITLE_MAPを使用する
// addressの各行の title（日本語）と英語のマッピング。
// 建物名・部屋番号（address3）はAZチェック対象外だが、他行との状態衝突を避けるため一意なfieldKeyを割り当てる
const JA_TO_EN_ADDRESS_ROW_TITLE_MAP: { [title: string]: string } = {
  '郵便番号・都道府県': 'zipAndPrefecture',
  '市区町村': 'address1',
  '丁目番地号': 'address2',
  '建物名・部屋番号': 'address3',
}

// 本社所在地の全行と、そのうちAZチェック対象の行
const ENTERPRISE_ADDRESS_FIELD_KEYS: string[] = ['zipAndPrefecture', 'address1', 'address2', 'address3'].map(
  (key) => `${ENTERPRISE_PREFIX}${key}`,
);
const ENTERPRISE_ADDRESS_CHECKED_FIELD_KEYS: string[] = ['zipAndPrefecture', 'address1', 'address2'].map(
  (key) => `${ENTERPRISE_PREFIX}${key}`,
);

const SEMINAR_AND_INTERVIEW_INFO_LABEL = 'セミナー・面接情報';

// 集約チェックの入力先（mach-api の AzCheckSeminarAndInterviewInfoValidator と対応。項目追加時は両方の更新が必要）
const SEMINAR_AND_INTERVIEW_INFO_INPUT_ROWS: { title: string; fieldKey: string }[] = [
  { title: 'セミナー選考情報', fieldKey: 'screeningInfoSeminar' },
  { title: '1次選考情報', fieldKey: 'screeningInfo1' },
  { title: 'セミナー案内', fieldKey: 'interviewInfoSeminar' },
  { title: '面接案内1', fieldKey: 'interviewInfo1' },
];

// 集約チェックはdynamic_fieldマスタに定義がないため組み立てる（参照されるのはfieldNameとlabelのみ）
const SEMINAR_AND_INTERVIEW_INFO_FIELD_DEFINITION: DynamicField = {
  fieldName: SEMINAR_AND_INTERVIEW_INFO_FIELD_KEY,
  fieldType: 'text',
  label: SEMINAR_AND_INTERVIEW_INFO_LABEL,
};

// 給与補足への反映に関わるsalaryTableの行。NGやコメントがなくても画面上で反映できるよう常に表示する
const ALWAYS_DISPLAYED_SALARY_TABLE_FIELD_KEYS: string[] = [
  'salary',
  'fixedOvertimeFee',
  'fixedOvertimeFeeDescription',
  'fixedLateNightOvertime',
  'variableWorkingHoursSystem',
  'salaryDescription',
].map((enTitle) => `${SALARY_TABLE_FIELD_KEY_PREFIX}${enTitle}`);

// NGやコメントの有無に関わらず常に表示したい項目のfieldKey一覧
const ALWAYS_DISPLAYED_FIELD_KEYS_FOR_NOMARL_JOB_CATEGORY: string[] = [
  'appeal1Title',
  'appeal1Body',
  'appeal2Title',
  'appeal2Body',
  ...ALWAYS_DISPLAYED_SALARY_TABLE_FIELD_KEYS,
];
const ALWAYS_DISPLAYED_FIELD_KEYS_FOR_IS_JOB_CATEGORY: string[] = ['appeal1Title', 'appeal1Body', 'appeal2Title', 'appeal2Body'];
const ALWAYS_DISPLAYED_FIELD_KEYS_FOR_OC_JOB_CATEGORY: string[] = [
  'appeal1Title',
  'appeal1Body',
  'appeal2Title',
  'appeal2Body',
  ...ALWAYS_DISPLAYED_SALARY_TABLE_FIELD_KEYS,
];
const ALWAYS_DISPLAYED_FIELD_KEYS_FOR_EARLY_JOB_CATEGORY: string[] = [];

export interface WageCheckFieldGroup {
  field: WageCheckField;
  label: string;
  details: JobAzCheckWageErrorDetail[];
}

@Injectable()
export class JobAzCheckService implements OnDestroy {
  job!: Job;
  companyData: CompanyDetail | null = null;
  checkResult: JobAzCheckResponse | null = null;
  // 今回のチェックで選択された種別
  selectedCategory: JobCategory | null = null;
  checkSections: AzCheckSection[] = [];
  // setCheckResultが呼ばれた時点で1回だけ計算し、以後は同じ参照を返す。
  // テンプレートの*ngForにgetterで都度新しい配列を返すと、変更検知の度にDOMが再生成され描画が詰まるため、
  // checkResultと同じタイミングで更新されるサービス側の状態として保持する。
  wageCheckFieldGroups: WageCheckFieldGroup[] = [];
  consultationTargets: { [fieldKey: string]: boolean } = {};
  previewPdf: Blob | null = null;
  previewPdfLoading = false;
  previewPdfError = false;
  pdfCheckAccepted = false;
  private fieldValidities: { [fieldKey: string]: boolean } = {};

  private fieldSubscription: Subscription | undefined;
  private _hasUnsavedChanges: boolean = false;
  // 連動して自動的に決まる項目（jobType1.code2など）の変化によりonFormChangedが発火しても、
  // 実際にデータが変化していなければ修正扱いにしないための基準値
  private pristineJob: Job | null = null;
  private pristineCompanyData: CompanyDetail | null = null;

  get hasUnsavedChanges(): boolean {
    return this._hasUnsavedChanges;
  }

  set hasUnsavedChanges(value: boolean) {
    this._hasUnsavedChanges = value;
    if (!value) {
      this.pristineJob = deepClone(this.job);
      this.pristineCompanyData = this.companyData ? deepClone(this.companyData) : null;
    }
  }

  get validity(): boolean {
    const keys = Object.keys(this.fieldValidities);
    for (const key of keys) {
      if (!this.fieldValidities[key]) {
        return false;
      }
    }
    return true;
  }

  get canSendToCompany(): boolean {
    if (!this.validity || this.hasUnsavedChanges) {
      return false;
    }

    const keys = Object.keys(this.consultationTargets);
    for (const key of keys) {
      if (this.consultationTargets[key]) {
        return false;
      }
    }

    return true;
  }

  constructor(
    private dynamicService: DynamicFieldService,
    private jobApiService: JobApiService,
    private companyApiService: CompanyApiService,
    private masterService: MasterApiService,
    private dialogService: DialogService,
    private dialog: DialogRef,
    private salaryTableRowsService: SalaryTableRowsService,
  ) {
    this.salaryTableRowsService.init(() => this.job.dynamicData.salaryTable, 'dynamicData.salaryTable');
  }

  init(job: Job): void {
    this.job = deepClone(job);
    this.pristineJob = deepClone(this.job);
    this.companyApiService.getDetail(this.job.enterpriseId).pipe(shareReplay(1)).subscribe(data => {
      this.companyData = data;
      this.pristineCompanyData = deepClone(data);
    });
    this.fieldSubscription = this.dynamicService.fieldUpdateEvent.subscribe(() => {
      this.buildCheckSections();
    });
  }

  ngOnDestroy(): void {
    if (this.fieldSubscription) {
      this.fieldSubscription.unsubscribe();
    }
  }

  buildCheckSections(): void {
    this.fieldValidities = {};
    const makeEnterpriseRows = (fieldNames: string[], prefix?: string, options: any = {}, extraFieldNames: string[] = []): AzCheckRow[] => {
      return fieldNames.reduce((acc, fieldName) => {
        let def = this.dynamicService.getDefinition('enterprise', fieldName);
        if (!def) {
          return acc;
        }

        if (options.displayType) {
          def = {...def, displayType: options.displayType};
        }

        const formRows = this.dynamicService.getFormRows(def, prefix, options.className);

        formRows.forEach((row: RowMeta) => {
          if (!row.title) {
            return;
          }
          if (fieldName === 'address') {
            // 住所の紐付けを外す（フォームが独立しているため紐付けでワーニングが出る事象を回避）
            for (let field of row.fields) {
              field.linkTo = undefined;
            }
          }

          extraFieldNames.forEach(extraFieldName => {
            const extraDef = this.dynamicService.getDefinition('enterprise', extraFieldName);
            if (!extraDef) {
              return;
            }
            const extraRows = this.dynamicService.getFormRows(extraDef, 'dynamicData');
            if (!extraRows[0]) {
              return;
            }
            row.fields.push(extraRows[0].fields[0]);
          });

          const addressRowFieldName = fieldName === 'address' && row.title ? JA_TO_EN_ADDRESS_ROW_TITLE_MAP[row.title] : undefined;

          acc.push({
            fieldKey: `${ENTERPRISE_PREFIX}${addressRowFieldName || fieldName}`,
            fieldDefinition: def,
            formMeta: { groups: [{ rows: [row] }] },
            useCompanyModel: true,
            companionFieldNames: extraFieldNames,
          });
        });
        return acc;
      }, [] as AzCheckRow[]);
    };

    const makeRows = (fieldNames: string[], prefix?: string, options: any = {}): AzCheckRow[] => {
      return fieldNames.reduce((acc, fieldName) => {
        let def = this.dynamicService.getDefinition('job', fieldName);
        if (!def) {
          return acc;
        }

        if (options.displayType) {
          def = {...def, displayType: options.displayType};
        }

        const formRows = this.dynamicService.getFormRows(def, prefix, options.className);
        formRows.forEach((row: RowMeta) => {
          if (!row.title) {
            return;
          }

          // 勤務先住所は「勤務地(雇入れ直後)」としてaddress1のみを表示する
          // address1に求人票に表示する住所を全て記載する運用（例：【雇入れ直後】東京都〇〇区〇〇1-2-3）
          if (fieldName === 'workplace') {
            if (row.fields[0].name !== 'dynamicData.workplace.address1') {
              return;
            }
            row.title = '勤務地(雇入れ直後)';
          }

          // 勤務地補足は「勤務地(変更の範囲)」として表示する
          if (fieldName === 'workplaceRemarks') {
            row.title = '勤務地(変更の範囲)';
          }

          // 勤務先_都道府県の個別設定
          if (fieldName === 'workplacePrefectures') {
            row.fields[0].type = 'prefecture-area';
            row.fields[0].class = 'full';
          }

          // フォームが独立している影響で何かに関連して変化する項目が連動しないため手動で設定する
          //  受動喫煙対策（労働条件明示）
          if (fieldName === 'passiveSmokingMeasureRule') {
            this.setPassiveSmokingRule(row);
          }

          //  試用期間
          if (fieldName === 'trialMonth') {
            this.setTrialMonth(row);
          }

          acc.push({
            fieldKey: fieldName,
            fieldDefinition: def,
            formMeta: {
              groups: [{ rows: [row] }],
            },
          });
        });
        return acc;
      }, [] as AzCheckRow[]);
    };

    this.checkSections = [
      {
        title: '概要',
        rows: [
          ...makeRows(['position', 'tagline'], 'dynamicData', { displayType: 'textarea' }),
        ],
      },
      {
        title: '企業概要',
        rows: [
          ...makeEnterpriseRows(['capital'], 'dynamicData', { className: 'half' }, ['capitalVisibility']),
          ...makeEnterpriseRows(['salesAmount'], 'dynamicData', { className: 'half' }, ['salesVisibility', 'salesGroupAmount']),
          ...makeEnterpriseRows(['salesDate'], 'dynamicData'),
          ...makeEnterpriseRows(['numberEmployees'], 'dynamicData', { className: 'half' }, ['numberGroupEmployees']),
          ...makeEnterpriseRows(['establishDate'], 'dynamicData'),
          ...makeEnterpriseRows(['marketName'], 'dynamicData', { className: 'half' }),
          ...makeEnterpriseRows(['url'], 'dynamicData', { className: 'full' }),
          ...makeEnterpriseRows(['companyInfo'], 'dynamicData'),
        ],
      },
      {
        title: '本社所在地',
        rows: makeEnterpriseRows(['address'], 'dynamicData'),
      },
      {
        title: '職務内容',
        rows: [
          ...makeRows(['jobType1'], 'dynamicData', { className: 'half' }),
          ...makeRows(['descriptionMainBody'], 'dynamicData'),
          ...makeRows(['description1Title'], 'dynamicData', { className: 'half' }),
          ...makeRows(['description1Body'], 'dynamicData'),
          ...makeRows(['description2Title'], 'dynamicData', { className: 'half' }),
          ...makeRows(['description2Body'], 'dynamicData'),
        ],
      },
      {
        title: '勤務先',
        rows: [
          ...makeRows([
            'workplace', 'workplaceRemarks', 'workplacePrefectures',
            'transfer', 'firstPlaceOfAssignment',
            ], 'dynamicData'),
          ...makeRows(['passiveSmokingMeasureType', 'passiveSmokingMeasureRule'], 'dynamicData', { className: 'full' }),
          ...makeRows(['passiveSmokingMeasureRemarks'], 'dynamicData'),
        ],
      },
      {
        title: '選考内容',
        rows: makeRows([
          'requirement', 'certificates',
          'interviewDescription', 'aptitudeTest', 'interviewCount',
        ], 'dynamicData'),
      },
      {
        title: 'アピールポイント',
        rows: [
          ...makeRows(['appealMainBody'], 'dynamicData'),
          ...makeRows(['appeal1Title'], 'dynamicData', { className: 'half' }),
          ...makeRows(['appeal1Body'], 'dynamicData'),
          ...makeRows(['appeal2Title'], 'dynamicData', { className: 'half' }),
          ...makeRows(['appeal2Body'], 'dynamicData'),
        ],
      },
      {
        title: '選考情報',
        rows: makeRows(['resumeRequiredOnRecommend', 'screeningArea', 'workArea'], 'dynamicData'),
      },
      {
        title: '性格・タイプ',
        rows: makeRows(['characterAndType1', 'characterAndType2', 'characterAndType3', 'characterAndType4', 'characterAndType5'], 'dynamicData'),
      },
      {
        title: '調整フローリンク',
        rows: makeRows(['adjustmentFlowLink'], 'dynamicData', { className: 'full' }),
      },
      {
        title: 'ステータス',
        rows: makeRows(['recruitYear'], 'dynamicData', { className: 'half' }),
      },
      {
        title: '採用条件',
        rows: [
          ...makeRows([
            'communicationSkill', 'impressionAndCharm', 'gender',
            'afterGraduate', 'postGraduate',
            'universityLevel', 'academicFieldType', 'academicCourseType',
            'roninYearType', 'repeatYearType', 'internationalStudent'
          ], 'dynamicData'),
        ]
      },
      {
        title: '待遇・福利厚生',
        rows: [
          ...makeRows(['employmentMethod', 'hasTrial', 'trialMonth', 'trialDescription', 'employmentPeriod', 'workingHourRange', 'workingHourComment'], 'dynamicData'),
          ...makeRows(['breakHour'], 'dynamicData', { className: 'full' }),
          ...makeRows(['overtimeType', 'overtimeHours', 'overtimeWork','paymentMethod'], 'dynamicData'),
          ...makeRows(['promotion'], 'dynamicData'),
          ...makeRows(['bonus'], 'dynamicData', { className: 'full' }),
          ...makeRows(['workhourType'], 'dynamicData', { className: 'half' }),
          ...makeRows(['hasSubmittedToLaborOfficeForDeemedWorkingHours'], 'dynamicData'),
          ...this.makeSalaryTableRows(),
          ...makeRows(['monthlyScheduledWorkingHours', 'annualScheduledWorkingHours', 'annualHolidays', 'weekendDescription', 'insurance'], 'dynamicData'),
          ...makeRows(['commuterDescription'], 'dynamicData', { className: 'full' }),
          ...makeRows(['supplementDescription', 'benefitDescription',], 'dynamicData'),
        ],
      },
      {
        title: SEMINAR_AND_INTERVIEW_INFO_LABEL,
        rows: [
          this.makeSeminarAndInterviewInfoRow(),
          ...SEMINAR_AND_INTERVIEW_INFO_INPUT_ROWS.map((inputRow) => this.makeScreeningInterviewRow(inputRow.title, inputRow.fieldKey)),
        ],
      },
      {
        title: 'その他',
        rows: makeRows(['secondMatchTarget'], 'dynamicData'),
      },
    ];
  }

  private hasInfoData(data: any): boolean {
    if (!data) {
      return false;
    }
    return Object.values(data).find((v) => !!v) != null;
  }

  private clearSectionInfo(data: any): void {
    if (!data) {
      return;
    }
    Object.keys(data).forEach((key) => (data[key] = null));
  }

  /**
   * salaryTable（月給・固定残業代・固定残業代補足・固定深夜割増手当・変形労働時間制・給与補足）の6行を生成する。
   * fieldKey には、salaryTable_ 接頭辞と row.title （日本語から英語にマッピングした値）を用いて行ごとに一意なfieldKeyを割り当てる。
   */
  private makeSalaryTableRows(): AzCheckRow[] {
    const def = this.dynamicService.getDefinition('job', 'salaryTable');
    if (!def) {
      return [];
    }
    const rows = this.dynamicService.getFormRows(def, 'dynamicData');
    this.salaryTableRowsService.rows = rows;
    this.salaryTableRowsService.wire();
    this.salaryTableRowsService.syncVisibility();
    this.salaryTableRowsService.setFixedOvertimeFeeDisabled(isDeemedWorkingHoursSystemSelected(this.job.dynamicData.workhourType));
    this.salaryTableRowsService.updateDescriptionValidation();
    return rows.map((row) => {
      // salaryTableの紐付けを外す（フォームが独立しているため紐付けでワーニングが出る事象を回避）
      for (let field  of row.fields) {
        field.linkTo = undefined;
      }

      const enTitle = row.title ? JA_TO_EN_SALARY_TABLE_ROW_TITLE_MAP[row.title] : undefined;
      return {
        fieldKey: `${SALARY_TABLE_FIELD_KEY_PREFIX}${enTitle}`,
        fieldDefinition: def,
        formMeta: { groups: [{ rows: [row] }] },
      }
    });
  }

  /**
   * セミナー・面接情報の集約チェック結果を表示する行を生成する。
   * 複数項目にまたがるチェックのため、入力欄を持たず項目名とコメントのみを表示する。
   */
  private makeSeminarAndInterviewInfoRow(): AzCheckRow {
    return {
      fieldKey: SEMINAR_AND_INTERVIEW_INFO_FIELD_KEY,
      fieldDefinition: SEMINAR_AND_INTERVIEW_INFO_FIELD_DEFINITION,
      formMeta: { groups: [{ rows: [{ title: SEMINAR_AND_INTERVIEW_INFO_LABEL, fields: [] }] }] },
    };
  }

  private makeScreeningInterviewRow(title: string, fieldKey: string): AzCheckRow {
    const def = this.dynamicService.getDefinition('job', fieldKey);
    const formRows = def ? this.dynamicService.getFormRows(def, 'dynamicData') : [];
    return {
      fieldKey,
      fieldDefinition: def,
      formMeta: {
        groups: [{
          collapsedTitle: title,
          toggleButtonLabel: '入力',
          toggledButtonLabel: '削除',
          toggledButtonAction: () => this.clearSectionInfo(this.job.dynamicData[fieldKey]),
          class: 'screening-interview',
          collapsable: true,
          expanded: this.hasInfoData(this.job.dynamicData[fieldKey]),
          rows: formRows,
        }],
      },
    };
  }

  isDisplaySection(section: AzCheckSection): boolean {
    if (!this.checkResult) {
      return false;
    }

    // セクション内に表示対象の行が1つでもあれば表示する（常に表示したい行がある場合もこれで表示される）
    return section.rows.some(row => this.isDisplayRow(row));
  }

  isDisplayRow(row: AzCheckRow): boolean {
    if (!this.checkResult) {
      return false;
    }

    // 求人種別ごとに無条件で表示する項目がある場合
    if (this.selectedCategory === 'normal' && ALWAYS_DISPLAYED_FIELD_KEYS_FOR_NOMARL_JOB_CATEGORY.includes(row.fieldKey)) {
      return true;
    } else if (this.selectedCategory === 'is' && ALWAYS_DISPLAYED_FIELD_KEYS_FOR_IS_JOB_CATEGORY.includes(row.fieldKey)) {
      return true;
    } else if (this.selectedCategory === 'oc' && ALWAYS_DISPLAYED_FIELD_KEYS_FOR_OC_JOB_CATEGORY.includes(row.fieldKey)) {
      return true;
    } else if (this.selectedCategory === 'early' && ALWAYS_DISPLAYED_FIELD_KEYS_FOR_EARLY_JOB_CATEGORY.includes(row.fieldKey)) {
      return true;
    }

    const errors  = this.checkResult.validationErrors;

    if (row.fieldKey === 'passiveSmokingMeasureType') {
      // 受動喫煙対策（就業場所）については専用のチェックがなく表示条件に当てはまることがないが、
      // 受動喫煙対策（労働条件明示）を選択するためには受動喫煙対策（就業場所）を設定する必要があるため、
      // 受動喫煙対策（労働条件明示）が表示対象の場合、受動喫煙対策（就業場所）も表示対象とする。
      return errors.details.some(detail => detail.field === 'passiveSmokingMeasureRule');
    }

    if (ENTERPRISE_ADDRESS_FIELD_KEYS.includes(row.fieldKey)) {
      // 住所全体を見ながら修正できるよう、いずれかの行が表示対象なら本社所在地の全行を表示する（アラートは該当行のみ）
      return errors.details.some((detail) => ENTERPRISE_ADDRESS_CHECKED_FIELD_KEYS.includes(detail.field));
    }

    if (SEMINAR_AND_INTERVIEW_INFO_INPUT_ROWS.some(inputRow => inputRow.fieldKey === row.fieldKey)) {
      // これらは専用のチェックを持たないが、集約チェックを解消するための入力先となるため、
      // 集約チェックが表示対象の場合は併せて表示対象とする。
      return errors.details.some(detail => detail.field === SEMINAR_AND_INTERVIEW_INFO_FIELD_KEY);
    }

    const hasDetail = errors.details.some(detail => detail.field === row.fieldKey);
    const hasSalaryTableDetail = errors.salaryTableDetails.some(detail => detail.field === row.fieldKey);
    // NG項目もしくは確認・注意を促すコメントがある場合のみ表示する
    return hasDetail || hasSalaryTableDetail;
  }

  requestAzCheck(category: JobCategory): Observable<JobAzCheckResponse> {
    this.selectedCategory = category;
    return this.jobApiService.azCheck(this.job.id, category);
  }

  executeAz(): Observable<{ id: number }> {
    return this.jobApiService.executeAz(this.job.id);
  }

  refreshAz(): Observable<{ id: number }> {
    return this.jobApiService.refreshAz(this.job.id);
  }

  setCheckResult(result: JobAzCheckResponse): void {
    this.checkResult = result;
    this.consultationTargets = {};
    this.wageCheckFieldGroups = this.buildWageCheckFieldGroups(result.validationErrors.wageDetails);
    this.pdfCheckAccepted = false;
    this.loadPreviewPdf();
  }

  loadPreviewPdf(): void {
    this.previewPdfLoading = true;
    this.previewPdfError = false;
    this.jobApiService.downloadPdf(this.job.id).subscribe({
      next: (resp) => {
        this.previewPdf = resp.body;
        this.previewPdfLoading = false;
      },
      error: () => {
        this.previewPdf = null;
        this.previewPdfLoading = false;
        this.previewPdfError = true;
      },
    });
  }

  private buildWageCheckFieldGroups(wageDetails: JobAzCheckWageErrorDetail[]): WageCheckFieldGroup[] {
    return WAGE_CHECK_FIELDS.map((field) => ({
      field: field,
      label: WAGE_CHECK_FIELD_LABELS[field],
      // detail.field はAPI側でwage_接頭辞と卒業種別を付与した一意キー（例: wage_breakHour_none）のため前方一致で判定する
      details: wageDetails.filter((detail) => detail.field.startsWith(`${WAGE_PREFIX}${field}_`)),
    })).filter((group) => group.details.length > 0);
  }

  getPassedCount(): number {
    if (!this.checkResult) {
      return 0;
    }
    const detailErrors = this.checkResult.validationErrors.details.filter(detail => detail.type === 'error');
    const salaryTableDetailsError = this.checkResult.validationErrors.salaryTableDetails.filter(detail => detail.type === 'error');
    const wageDetailErrors = this.checkResult.validationErrors.wageDetails.filter(detail => detail.type === 'error');
    const errorCount = detailErrors.length + wageDetailErrors.length + salaryTableDetailsError.length;
    return this.checkResult.totalFieldsCount - errorCount;
  }

  getProgressPercent(): number {
    if (!this.checkResult || this.checkResult.totalFieldsCount === 0) {
      return 0;
    }
    return (this.getPassedCount() / this.checkResult.totalFieldsCount) * 100;
  }

  getError(fieldKey: string): JobAzCheckErrorDetail | undefined {
    if (!this.checkResult) {
      return undefined;
    }
    return this.checkResult.validationErrors.details.find((d) => d.field === fieldKey) ||
      this.checkResult.validationErrors.salaryTableDetails.find((d) => d.field === fieldKey);
  }

  onFormChanged(key: string, row: AzCheckRow, azCheckSectionRows: AzCheckRow[]): void {
    this.refreshUnsavedChangesState();
    // フォームが独立している影響で本来連動して変化する項目が変化しないため手動で設定する
    if (row.useCompanyModel && this.companyData) {
      // 企業関連で制御する必要がある場合
      return;
    } else {
      // 企業以外で制御する必要がある場合
      if (key === 'dynamicData.jobType1.code1') {
        const azCheckRow = this.getAzCheckRowByFieldKey(azCheckSectionRows, 'jobType1');
        if (!azCheckRow) return;
        this.setJobTypeCode2(azCheckRow.formMeta.groups[0].rows[0], this.job.dynamicData.jobType1.code1, azCheckRow);

      } else if (key === 'dynamicData.passiveSmokingMeasureType') {
        const azCheckRow = this.getAzCheckRowByFieldKey(azCheckSectionRows, 'passiveSmokingMeasureRule');
        if (!azCheckRow) return;
        this.setPassiveSmokingRule(azCheckRow.formMeta.groups[0].rows[0]);

      } else if (key === 'dynamicData.hasTrial') {
        const azCheckRow = this.getAzCheckRowByFieldKey(azCheckSectionRows, 'trialMonth');
        if (!azCheckRow) return;
        this.setTrialMonth(azCheckRow.formMeta.groups[0].rows[0]);

      } else if (key === 'dynamicData.salaryTable.fixedOvertimeFee' && this.job.dynamicData.salaryTable.fixedOvertimeFee === '無') {
        clearFixedOvertimeCategoryValues(this.job.dynamicData.salaryTable);
        this.salaryTableRowsService.syncVisibility();
        // 固定残業代を'無'に変更した際に深夜割増手当も null となり、給与補足に変更が入るため給与補足の表示を更新する
        this.updateSalaryDescription(azCheckSectionRows);
        // 固定残業代補足（非編集テキスト）もクリアされるため、編集可能欄の文字数上限を更新する
        this.refreshFixedOvertimeDescriptionRow(azCheckSectionRows);

      } else if (key.startsWith('dynamicData.salaryTable.fixedOvertimeDescription')) {
        // 「反映」「短縮反映」「クリア」ボタン押下、または固定残業代補足の編集可能欄への入力があった際に文字数上限を更新する
        this.refreshFixedOvertimeDescriptionRow(azCheckSectionRows);

      } else if (
        key === 'dynamicData.salaryTable.fixedLateNightOvertimeType' &&
        this.job.dynamicData.salaryTable.fixedLateNightOvertimeType !== '有'
      ) {
        clearFixedLateNightOvertimeCategoryValues(this.job.dynamicData.salaryTable);
        this.salaryTableRowsService.syncVisibility();
        // 固定の深夜割増手当有無を'有'以外に変更した際に給与補足に変更が入るため給与補足の表示を更新する
        this.updateSalaryDescription(azCheckSectionRows);

      } else if (key === 'dynamicData.salaryTable.variableWorkingHoursSystem') {
        this.job.dynamicData.salaryTable.salaryDescriptionVariableWorkingHoursSystem = formatVariableWorkingHoursSystemDescription(this.job.dynamicData.salaryTable.variableWorkingHoursSystem);
        // 変形労働時間制を変更した際に給与補足に変更が入るため給与補足の表示を更新する
        this.updateSalaryDescription(azCheckSectionRows);

      } else if (key.startsWith('dynamicData.salaryTable.salaryDescription')) {
        // 「給与補足へ反映」ボタン等で給与補足の内容に変更があった際に給与補足欄の表示を更新する（月給または固定の深夜割増欄にある反映、短縮反映、クリアボタン押下時）
        this.updateSalaryDescription(azCheckSectionRows);

      } else if (key === 'dynamicData.workhourType') {
        this.applyDeemedWorkingHoursSystemFixedOvertimeConstraint(azCheckSectionRows);

      }
    }
  }

  onActioned(key: string, actionType: string): void {
    // フォームが独立している影響で本来連動して変化する項目が変化しないため手動で設定する
    if (actionType === 'UPDATE_LINKED') {
      const zip = key === 'dynamicData.workplace.zip.1' ? this.job.dynamicData.workplace.zip : this.companyData && this.companyData.dynamicData.address.zip;
      if (zip.length !== 7) return;

      this.masterService.getAddressByZip(zip).subscribe({
        next: response => {
          const address = response.results[0];
          if (!address) return;

          if (key === 'dynamicData.workplace.zip.1') {
            this.job.dynamicData.workplace.prefecture = address.prefecture;
            this.job.dynamicData.workplace.address1 = address.address1;
            this.job.dynamicData.workplace.address2 = address.address2;
          } else if (key === 'dynamicData.address.zip.1' && this.companyData) {
            this.companyData.dynamicData.address.prefecture = address.prefecture;
            this.companyData.dynamicData.address.address1 = address.address1;
            this.companyData.dynamicData.address.address2 = address.address2;
          }

          this.refreshUnsavedChangesState();
        },
        error: err => { this.dialog.close(); }
      });
    }
  }

  onValidityChange(validity: boolean, key: string): void {
    // フォームが独立している影響などでvalidityが想定外の値となるものについては個別で設定する
    if (key === 'passiveSmokingMeasureRule') {
      // フォームの全コントロールが disabled の場合、FormGroup.status が DISABLED となり valid === false になるため
      // 実際のフォームの有効性と disabled 状態を合わせて判定する
      this.fieldValidities[key] = validity || this.isRowAllDisabled(key);
      return;

    } else if (key === 'trialMonth') {
      if (this.job.dynamicData.hasTrial !== '有') {
        // 試用期間有ではない場合、試用期間は必須ではないためtrueとする
        this.fieldValidities[key] = true;
        return;
      } else {
        // 試用期間有の場合、期間が入力されているかによって判定する。
        this.fieldValidities[key] = !!this.job.dynamicData.trialMonth;
        return;
      }

    } else if (key === `${SALARY_TABLE_FIELD_KEY_PREFIX}fixedOvertimeFee`) {
      this.fieldValidities[key] = validity || this.isRowAllDisabled(key);
      return;

    }

    this.fieldValidities[key] = validity;
  }

  /**
   * 指定された項目のsalaryTable行の表示内容を更新する
   */
  refreshSalaryTableRow(title: string): void {
    if (title === '固定の深夜割増手当' || title === '変形労働時間制') {
      // 固定の深夜割増手当と変形労働時間制に修正があった場合給与補足へも修正が入るため表示内容を更新する
      const targetSection = this.checkSections.find(section => section.title === '待遇・福利厚生');
      if (!targetSection) return;
      this.updateSalaryDescription(targetSection.rows);
    }

    this.salaryTableRowsService.syncVisibility();
  }

  private updateSalaryDescription(rows: AzCheckRow[]): void {
    this.salaryTableRowsService.updateDescriptionValidation();
    const salaryDescriptionRow = this.getAzCheckRowByFieldKey(rows, `${SALARY_TABLE_FIELD_KEY_PREFIX}${JA_TO_EN_SALARY_TABLE_ROW_TITLE_MAP['給与補足']}`);
    this.refreshFormMeta(salaryDescriptionRow);
  }

  /**
   * 固定残業代補足行（固定残業代補足のfieldKey）のformMetaを再構築し、文字数上限の変更をフォームへ反映する。
   */
  private refreshFixedOvertimeDescriptionRow(azCheckSectionRows: AzCheckRow[]): void {
    this.salaryTableRowsService.updateDescriptionValidation();
    const fixedOvertimeDescriptionRow = this.getAzCheckRowByFieldKey(
      azCheckSectionRows,
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JA_TO_EN_SALARY_TABLE_ROW_TITLE_MAP['固定残業代補足']}`,
    );
    this.refreshFormMeta(fixedOvertimeDescriptionRow);
  }

  /**
   * みなし労働時間制の有無（dynamicData.workhourType）が「有」から始まる場合、
   * 固定残業代を「無」に強制してクリアし、固定残業代の入力を非活性にする。「有」以外の場合は活性に戻す。
   */
  private applyDeemedWorkingHoursSystemFixedOvertimeConstraint(azCheckSectionRows: AzCheckRow[]): void {
    const disabled = isDeemedWorkingHoursSystemSelected(this.job.dynamicData.workhourType);
    if (disabled) {
      forceFixedOvertimeFeeNoneForDeemedWorkingHours(this.job.dynamicData.salaryTable);
      this.salaryTableRowsService.syncVisibility();
      // 固定残業代を'無'に変更した際に深夜割増手当も null となり、給与補足に変更が入るため給与補足の表示を更新する
      this.updateSalaryDescription(azCheckSectionRows);
      // 固定残業代補足（非編集テキスト）もクリアされるため、編集可能欄の文字数上限を更新する
      this.refreshFixedOvertimeDescriptionRow(azCheckSectionRows);
    }
    this.syncFixedOvertimeFeeDisabledState();
  }

  /**
   * みなし労働時間制の有無に応じた固定残業代の活性/非活性状態を同期する。
   */
  syncFixedOvertimeFeeDisabledState(): void {
    const disabled = isDeemedWorkingHoursSystemSelected(this.job.dynamicData.workhourType);
    this.salaryTableRowsService.setFixedOvertimeFeeDisabled(disabled);
  }

  private getAzCheckRowByFieldKey(azCheckSectionRows: AzCheckRow[], fieldKey: string): AzCheckRow | undefined {
    return azCheckSectionRows.find(row => row.fieldKey === fieldKey) || undefined;
  }

  private setPassiveSmokingRule(row: RowMeta): void {
    const passiveSmokingMeasureType = this.job.dynamicData.passiveSmokingMeasureType;
    switch (passiveSmokingMeasureType) {
      case '第一種施設（学校・官公庁・病院等）':
        row.fields[0].options = ['敷地内禁煙', '敷地内禁煙（喫煙場所：有）', 'その他'];
        break;
      case '第二種施設（事業所・ホテル・旅館・飲食店等）':
        row.fields[0].options = [
          '屋内禁煙',
          '屋内禁煙（喫煙場所：有）',
          '屋内原則禁煙（喫煙室：有）',
          '屋内原則禁煙（喫煙可の宿泊室：有）',
          'その他',
        ];
        break;
      case '喫煙目的施設（バー・スナック・たばこ販売店等）':
        row.fields[0].options = ['屋内喫煙可', '屋内喫煙可（喫煙室内に限る）', 'その他'];
        break;
      case '旅客運送事業（バス・飛行機・電車・船舶）':
        row.fields[0].options = ['車内禁煙', '車内原則禁煙（喫煙室：有）', 'その他'];
        break;
      case '屋外（第一種施設の敷地内は除く）':
        row.fields[0].options = ['屋外のため規定なし'];
        break;
    }
    row.fields[0].disabled = passiveSmokingMeasureType === null;
  }

  private setTrialMonth(row: RowMeta): void {
    const hasTrial = this.job.dynamicData.hasTrial;
    row.fields[0].disabled = hasTrial !== '有';
    row.showRequired = hasTrial === '有';
  }

  private setJobTypeCode2(row: RowMeta, jobType: string, azCheckRow: AzCheckRow): void {
    const nullOption = { code: NULL_SELECTED_VALUE, name: NULL_SELECTED_VALUE };

    if (!jobType) {
      row.fields[1].options = [];
      row.fields[1].disabled = true;
      this.refreshFormMeta(azCheckRow);
      return;
    }

    const jobTypeOptions = row.fields[0].options || [];
    for (const option of jobTypeOptions) {
      if (option.code === jobType) {
        row.fields[1].options = [nullOption, ...option.jobTypes];
        row.fields[1].disabled = option.jobTypes.length === 0;
        this.refreshFormMeta(azCheckRow);
        return;
      }
    }
  }

  private isRowAllDisabled(fieldKey: string): boolean {
    for (const section of this.checkSections) {
      const azCheckRow = section.rows.find(r => r.fieldKey === fieldKey);
      if (!azCheckRow) {
        continue;
      }
      return azCheckRow.formMeta.groups.every(group =>
        group.rows.every(r => r.fields.every(f => f && f.disabled))
      );
    }
    return false;
  }

  // optionsを変更した際に DynamicFormComponent.ngDoCheck() の metadataDiffer が変化を検知できず、
  // フォームが再構築されない事象を回避するため groups の参照を変える。
  private refreshFormMeta = (azCheckRow?: AzCheckRow) => {
    if (!azCheckRow) return;
    azCheckRow.formMeta = { ...azCheckRow.formMeta, groups: [...azCheckRow.formMeta.groups] };
  };

  // 連動して自動的に決まる項目（jobType1.code2など）の変化によりonFormChangedが発火しても、
  // pristineJob/pristineCompanyDataと比較して実際にデータが変化していない場合は修正扱いにしない。
  private refreshUnsavedChangesState(): void {
    setTimeout(() => {
      const jobChanged = !deepCompare(this.job, this.pristineJob);
      const companyChanged = !!this.companyData && !deepCompare(this.companyData, this.pristineCompanyData);
      this._hasUnsavedChanges = jobChanged || companyChanged;
    });
  }
}
